/**
 * StaffDetail — EntityDetailShell-based Staff detail page (11E.4).
 *
 * Cross-feature composition (students-assigned, calendar, bookings tabs) is
 * injected via render-props so People does not import sibling features,
 * same pattern as StudentDetail.
 *
 * Salary/Pay tab is admin/owner-only — gated via useAuth().user.role.
 */

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '@/components/common/Card';
import { PageContainer } from '@/components/layout';
import { EntityDetailShell } from '@/components/entity';
import { useStaffMember, useUpdateStaff } from '@features/people/api/staff';
import { StaffFormModal } from '@features/people/components/StaffFormModal';
import { StaffDetailSidebar } from '@features/people/components/StaffDetailSidebar';
import { StaffTimesheetTab } from '@features/people/components/StaffTimesheetTab';
import { StaffPayRatesTab } from '@features/people/components/StaffPayRatesTab';
import { StaffWorkingHoursTab } from '@features/people/components/StaffWorkingHoursTab';
import { buildStaffDetailConfig } from '@features/people/config/staffEntityConfig';
import { useAuth } from '@/context';

interface StaffDetailProps {
  renderStudentsAssignedTab?: (staffId: string) => React.ReactNode;
  renderWorkingHoursTab?: (staffId: string) => React.ReactNode;
  renderCalendarTab?: (staffId: string) => React.ReactNode;
  renderBookingsTab?: (staffId: string) => React.ReactNode;
  renderAttendanceLeavesTab?: (staffId: string) => React.ReactNode;
  renderNotesTab?: (staffId: string) => React.ReactNode;
  renderDocumentsTab?: (staffId: string) => React.ReactNode;
  renderSalaryPayTab?: (staffId: string) => React.ReactNode;
  /** 11F.7 — cross-feature Insight cells for the left rail. */
  renderInsights?: (staffId: string) => React.ReactNode;
  /** 11F.7 — subjects for the pay-rate grid, injected from Catalog. */
  payRateSubjects?: { id: string; name: string }[];
}

export const StaffDetail: React.FC<StaffDetailProps> = ({
  renderInsights,
  payRateSubjects,
  ...renderProps
}) => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const tenantId = user?.tenant_id;
  const [isEditOpen, setIsEditOpen] = useState(false);

  const { data: staff, isLoading, isError } = useStaffMember(id ?? '');
  const updateStaff = useUpdateStaff();

  const handlePhotoUpload = async (url: string) => {
    if (!staff) return;
    await updateStaff.mutateAsync({ id: staff.id, body: { photo_url: url } });
  };

  if (isLoading) {
    return (
      <PageContainer title="Loading">
        <Card padding="lg"><p style={{ color: '#64748b' }}>Loading staff member…</p></Card>
      </PageContainer>
    );
  }

  if (isError || !staff) {
    return (
      <PageContainer title="Staff Member Not Found">
        <Card padding="lg">
          <p>The staff member you are looking for does not exist.</p>
        </Card>
      </PageContainer>
    );
  }

  // 11F.7 — identity/bio/expertise moved to the left rail; the Overview
  // body now leads with teaching load, which is what an admin opens a staff
  // record to check.
  const overviewContent = (
    <div data-testid="candeza-staff-overview">
      <StaffTimesheetTab staffId={staff.id} />
    </div>
  );

  const canEditStaff = user?.role === 'owner' || user?.role === 'admin';

  const detailConfig = buildStaffDetailConfig({
    renderOverviewTab: () => overviewContent,
    viewerRole: user?.role,
    renderTimesheetTab: (staffId) => <StaffTimesheetTab staffId={staffId} />,
    renderPayRatesTab: (staffId) => (
      <StaffPayRatesTab
        staffId={staffId}
        subjects={payRateSubjects}
        canEdit={canEditStaff}
      />
    ),
    renderWorkingHoursTab: (staffId) => (
      <StaffWorkingHoursTab staffId={staffId} canEdit={canEditStaff} />
    ),
    renderSidebar: (s) => (
      <StaffDetailSidebar staff={s} renderInsights={renderInsights} />
    ),
    ...renderProps,
  });

  return (
    <>
      <EntityDetailShell
        entity={staff}
        config={detailConfig}
        tenantId={tenantId ?? undefined}
        onEdit={() => setIsEditOpen(true)}
        onPhotoUpload={handlePhotoUpload}
        canEdit
      />

      <StaffFormModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        staff={staff}
      />
    </>
  );
};
